/* ============================================================
   Dathent — Admin Referrals review screen (/app/admin/referrals)
   Gated server-side by admin email allowlist. Lists waitlist
   signups + payout requests, allows marking payouts paid.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, DathentAPIError } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { referralProgramV2Enabled } from '../lib/flags.js';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtEur(cents) {
  return `€${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function StatusPill({ status }) {
  const cls = {
    waitlist: 'rr-pill-waitlist',
    activated: 'rr-pill-activated',
    churned: 'rr-pill-churned',
    requested: 'rr-pill-waitlist',
    processing: 'rr-pill-waitlist',
    paid: 'rr-pill-activated',
    cancelled: 'rr-pill-churned',
    held: 'rr-pill-waitlist',
    available: 'rr-pill-activated',
  }[status] || 'rr-pill-churned';
  return (
    <span className={`pill ${cls}`}>
      <span className="dot" /> {status}
    </span>
  );
}

// Credit rows are stored 'held'; getReferrerBalances lazily matures them past
// hold_until. Mirror that here so the ledger status matches the partner balance.
function effectiveCreditStatus(c) {
  if (c.status !== 'held') return c.status;
  if (c.holdUntil) {
    try { if (new Date(c.holdUntil) <= new Date()) return 'available'; } catch (_) { /* keep held */ }
  }
  return 'held';
}

function CreditsTable({ rows, filter, setFilter, loading }) {
  const { t } = useTranslation();
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>{t('adminReferrals.v2.commissions', 'Commissions ledger')}</span></div>
          <h2>{t('adminReferrals.v2.commissionsSubtitle', 'Every accrued commission — held, available, paid out')}</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'all', label: t('adminReferrals.tabAll') },
            { id: 'held', label: t('adminReferrals.v2.tabHeld', 'Held') },
            { id: 'available', label: t('adminReferrals.v2.tabAvailable', 'Available') },
          ].map((tab) => (
            <button key={tab.id} type="button" className={`rr-tab ${filter === tab.id ? 'is-active' : ''}`} onClick={() => setFilter(tab.id)}>{tab.label}</button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="rr-table-empty">{t('adminReferrals.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="rr-table-empty">{t('adminReferrals.v2.noCommissions', 'No commissions yet')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colReferrer')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colRole', 'Role')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colAmount')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colBase', 'Net base')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colStatus')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colCreated', 'Created')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const eff = effectiveCreditStatus(c);
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 6px' }}>
                      <div style={{ fontWeight: 500 }}>{c.referrerName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{c.referrerCode}</div>
                    </td>
                    <td style={{ padding: '10px 6px' }}>{c.role}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>
                      {fmtEur(c.amountCents)} {c.currency}
                      <span style={{ color: 'var(--text-subtle)', fontWeight: 400, fontSize: 11 }}> ({c.ratePercent}%)</span>
                    </td>
                    <td style={{ padding: '10px 6px', color: 'var(--text-muted)' }}>{c.netBaseCents != null ? fmtEur(c.netBaseCents) : '—'}</td>
                    <td style={{ padding: '10px 6px' }}>
                      <StatusPill status={eff} />
                      {eff === 'held' && c.holdUntil ? (
                        <span style={{ fontSize: 11, color: 'var(--text-subtle)', marginLeft: 6 }}>{t('adminReferrals.v2.until', 'until')} {fmtDate(c.holdUntil)}</span>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(c.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Stats({ data }) {
  const { t } = useTranslation();
  if (!data) return null;
  const cells = [
    { l: t('adminReferrals.totalSignups'), v: data.total_signups, sub: t('adminReferrals.signupsBreakdown', { waitlist: data.waitlist, activated: data.activated }) },
    { l: t('adminReferrals.referrers'), v: data.total_referrers, sub: t('adminReferrals.withProfiles') },
    { l: t('adminReferrals.pendingPayouts'), v: data.pending_payouts, sub: fmtEur(data.pending_amount_cents) },
  ];
  return (
    <div className="rr-stats">
      {cells.map((c) => (
        <div key={c.l} className="card rr-stat">
          <div className="rr-stat-l">{c.l}</div>
          <div className="rr-stat-v">{c.v}</div>
          <div className="rr-stat-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SignupsTable({ rows, filter, setFilter, loading }) {
  const { t } = useTranslation();
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>{t('adminReferrals.signups')}</span></div>
          <h2>{t('adminReferrals.signupsSubtitle')}</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'all', label: t('adminReferrals.tabAll') },
            { id: 'waitlist', label: t('adminReferrals.tabWaitlist') },
            { id: 'registered', label: t('adminReferrals.tabRegistered') },
            { id: 'activated', label: t('adminReferrals.tabActivated') },
            { id: 'churned', label: t('adminReferrals.tabChurned') },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`rr-tab ${filter === tab.id ? 'is-active' : ''}`}
              onClick={() => setFilter(tab.id)}
            >{tab.label}</button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="rr-table-empty">{t('adminReferrals.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="rr-table-empty">{t('adminReferrals.noSignups')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colEmail')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colReferrer')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colContact')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colLocale')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colStatus')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colJoined')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 6px', wordBreak: 'break-all' }}>{r.email}</td>
                  <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 500 }}>{r.referrerName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{r.referrerCode}</div>
                  </td>
                  <td style={{ padding: '10px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {r.contactValue ? <>{r.contactValue} <span style={{ color: 'var(--text-subtle)' }}>({r.contactKind})</span></> : '—'}
                  </td>
                  <td style={{ padding: '10px 6px' }}>{(r.locale || 'en').toUpperCase()}</td>
                  <td style={{ padding: '10px 6px' }}><StatusPill status={r.status} /></td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PayoutsTable({ rows, filter, setFilter, onMarkPaid, busyId }) {
  const { t } = useTranslation();
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>{t('adminReferrals.payouts')}</span></div>
          <h2>{t('adminReferrals.payoutsSubtitle')}</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'all', label: t('adminReferrals.tabAll') },
            { id: 'requested', label: t('adminReferrals.tabRequested') },
            { id: 'paid', label: t('adminReferrals.tabPaid') },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`rr-tab ${filter === tab.id ? 'is-active' : ''}`}
              onClick={() => setFilter(tab.id)}
            >{tab.label}</button>
          ))}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="rr-table-empty">{t('adminReferrals.noPayouts')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colReferrer')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colAmount')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colMethod')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colStatus')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colRequested')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colPaid')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 6px' }}>
                    <div style={{ fontWeight: 500 }}>{p.referrerName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{p.referrerCode}</div>
                  </td>
                  <td style={{ padding: '10px 6px', fontWeight: 600 }}>{fmtEur(p.amountCents)} {p.currency}</td>
                  <td style={{ padding: '10px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.method || '—'}</td>
                  <td style={{ padding: '10px 6px' }}><StatusPill status={p.status} /></td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(p.requestedAt)}</td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(p.paidAt)}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                    {p.status === 'requested' && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => onMarkPaid(p)}
                        disabled={busyId === p.id}
                      >
                        {busyId === p.id ? '…' : t('adminReferrals.markPaid')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MarkPaidModal({ payout, onClose, onSubmit }) {
  const [method, setMethod] = useState(payout?.method || 'bank');
  const [details, setDetails] = useState(payout?.methodDetails || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const { t } = useTranslation();

  if (!payout) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ method, methodDetails: details, notes });
      onClose();
    } catch (e) {
      setErr(e?.message || t('adminReferrals.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-elev)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 460, width: '100%',
        }}
      >
        <h3 style={{ margin: '0 0 4px' }}>{t('adminReferrals.markPayoutPaid')}</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          {payout.referrerName} ({payout.referrerCode}) · {fmtEur(payout.amountCents)} {payout.currency}
        </p>

        <div className="rr-field">
          <label className="rr-l">{t('adminReferrals.method')}</label>
          <select className="rr-input" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="bank">{t('adminReferrals.methodBank')}</option>
            <option value="paypal">PayPal</option>
            <option value="crypto">{t('adminReferrals.methodCrypto')}</option>
            <option value="other">{t('adminReferrals.methodOther')}</option>
          </select>
        </div>
        <div className="rr-field" style={{ marginTop: 12 }}>
          <label className="rr-l">{t('adminReferrals.details')}</label>
          <input
            className="rr-input"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t('adminReferrals.detailsPlaceholder')}
            maxLength={500}
          />
        </div>
        <div className="rr-field" style={{ marginTop: 12 }}>
          <label className="rr-l">{t('adminReferrals.notes')}</label>
          <textarea
            className="rr-input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </div>

        {err && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t('adminReferrals.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? '…' : t('adminReferrals.confirmPaid')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Referral v2 — application moderation queue + partner registry ─────────── */
function AppStatusPill({ status }) {
  const cls = {
    review: 'rr-pill-waitlist',
    needs_data: 'rr-pill-waitlist',
    active: 'rr-pill-activated',
    rejected: 'rr-pill-churned',
    none: 'rr-pill-churned',
  }[status] || 'rr-pill-churned';
  return <span className={`pill ${cls}`}><span className="dot" /> {status}</span>;
}

function ReviewModal({ app, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [action, setAction] = useState('approve');
  const [tier, setTier] = useState(app?.desiredTier === 'master' ? 'master' : 'regular');
  const [parent, setParent] = useState(app?.parentReferrerId || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!app) return null;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onSubmit({
        action,
        partnerType: action === 'approve' ? tier : undefined,
        parentReferrerId: action === 'approve' && parent.trim() ? parent.trim() : undefined,
        reason: action !== 'approve' ? (reason.trim() || null) : undefined,
      });
      onClose();
    } catch (e) {
      setErr(e?.message || t('adminReferrals.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 460, width: '100%' }}>
        <h3 style={{ margin: '0 0 4px' }}>{t('adminReferrals.v2.reviewTitle', 'Review application')}</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          {app.userName || app.userEmail || app.displayName} · <span style={{ fontFamily: 'var(--font-mono)' }}>{app.code}</span>
          {app.desiredTier ? ` · ${t('adminReferrals.v2.wants', 'wants')} ${app.desiredTier}` : ''}
        </p>

        <div className="rr-field">
          <label className="rr-l">{t('adminReferrals.v2.decision', 'Decision')}</label>
          <select className="rr-input" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="approve">{t('adminReferrals.v2.approve', 'Approve')}</option>
            <option value="needs_data">{t('adminReferrals.v2.needsData', 'Request more info')}</option>
            <option value="reject">{t('adminReferrals.v2.reject', 'Reject')}</option>
          </select>
        </div>

        {action === 'approve' ? (
          <>
            <div className="rr-field" style={{ marginTop: 12 }}>
              <label className="rr-l">{t('adminReferrals.v2.assignTier', 'Assign tier')}</label>
              <select className="rr-input" value={tier} onChange={(e) => setTier(e.target.value)}>
                <option value="regular">{t('adminReferrals.v2.tierRegular', 'Regular (10%)')}</option>
                <option value="master">{t('adminReferrals.v2.tierMaster', 'Master (20% + 10% override)')}</option>
              </select>
            </div>
            <div className="rr-field" style={{ marginTop: 12 }}>
              <label className="rr-l">{t('adminReferrals.v2.attachMaster', 'Attach to Master (referrer id, optional)')}</label>
              <input className="rr-input" value={parent} onChange={(e) => setParent(e.target.value)} placeholder="rref_…" maxLength={80} />
            </div>
          </>
        ) : (
          <div className="rr-field" style={{ marginTop: 12 }}>
            <label className="rr-l">{t('adminReferrals.v2.reason', 'Reason / note (shown to applicant)')}</label>
            <textarea className="rr-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          </div>
        )}

        {err && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t('adminReferrals.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? '…' : t('adminReferrals.v2.confirm', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApplicationsTable({ rows, scope, setScope, loading, onReview, onTogglePause, busyId }) {
  const { t } = useTranslation();
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>{t('adminReferrals.v2.applications', 'Partner applications')}</span></div>
          <h2>{t('adminReferrals.v2.applicationsSubtitle', 'Review applications, assign tiers, manage partners')}</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'pending', label: t('adminReferrals.v2.tabPending', 'Pending') },
            { id: 'all', label: t('adminReferrals.v2.tabAllPartners', 'All partners') },
          ].map((tab) => (
            <button key={tab.id} type="button" className={`rr-tab ${scope === tab.id ? 'is-active' : ''}`} onClick={() => setScope(tab.id)}>{tab.label}</button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="rr-table-empty">{t('adminReferrals.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="rr-table-empty">{t('adminReferrals.v2.noApplications', 'No applications')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colApplicant', 'Applicant')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colTier', 'Tier')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colEntity', 'Entity')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colContact')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.colStatus')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>{t('adminReferrals.v2.colUpdated', 'Updated')}</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 6px' }}>
                    <div style={{ fontWeight: 500 }}>{a.userName || a.displayName || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{a.userEmail || ''}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{a.code}</div>
                  </td>
                  <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 500 }}>{a.partnerType || 'regular'}</span>
                    {a.desiredTier && a.desiredTier !== a.partnerType && a.applicationStatus !== 'active' && (
                      <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}> ({t('adminReferrals.v2.wants', 'wants')} {a.desiredTier})</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 6px' }}>{a.legalEntityType || '—'}</td>
                  <td style={{ padding: '10px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{a.contactValue || '—'}</td>
                  <td style={{ padding: '10px 6px' }}><AppStatusPill status={a.applicationStatus} /></td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(a.updatedAt)}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(a.applicationStatus === 'review' || a.applicationStatus === 'needs_data') && (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => onReview(a)} disabled={busyId === a.id}>
                        {busyId === a.id ? '…' : t('adminReferrals.v2.reviewBtn', 'Review')}
                      </button>
                    )}
                    {a.applicationStatus === 'active' && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onTogglePause(a)} disabled={busyId === a.id}>
                        {busyId === a.id ? '…' : (a.isPaused ? t('adminReferrals.v2.resume', 'Resume') : t('adminReferrals.v2.pause', 'Pause'))}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function AdminReferrals() {
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [signupFilter, setSignupFilter] = useState('all');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [loadingSignups, setLoadingSignups] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { t } = useTranslation();

  // Referral v2 — application moderation (dark until the flag is flipped).
  const v2 = referralProgramV2Enabled();
  const [applications, setApplications] = useState([]);
  const [appsScope, setAppsScope] = useState('pending');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [credits, setCredits] = useState([]);
  const [creditFilter, setCreditFilter] = useState('all');

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [s, sg, py] = await Promise.all([
        api.adminReferral.stats(),
        api.adminReferral.listSignups({ status: signupFilter }),
        api.adminReferral.listPayouts({ status: payoutFilter }),
      ]);
      setStats(s.stats || null);
      setSignups(sg.signups || []);
      setPayouts(py.payouts || []);
      if (v2) {
        const [apps, cr] = await Promise.all([
          api.adminReferral.listApplications({ scope: appsScope }).catch(() => ({ applications: [] })),
          api.adminReferral.listCredits({ status: creditFilter }).catch(() => ({ credits: [] })),
        ]);
        setApplications(apps.applications || []);
        setCredits(cr.credits || []);
      }
    } catch (err) {
      if (err instanceof DathentAPIError && (err.status === 401 || err.status === 403)) {
        setForbidden(true);
      } else {
        setError(err?.message || t('adminReferrals.failedToLoad'));
      }
    } finally {
      setLoadingSignups(false);
    }
  }, [signupFilter, payoutFilter, v2, appsScope, creditFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onMarkPaid = (payout) => setMarkPaidTarget(payout);
  const submitMarkPaid = async (patch) => {
    setBusyId(markPaidTarget.id);
    try {
      await api.adminReferral.markPayoutPaid(markPaidTarget.id, patch);
      await loadAll();
    } finally {
      setBusyId(null);
    }
  };

  const submitReview = async (patch) => {
    setBusyId(reviewTarget.id);
    try {
      await api.adminReferral.reviewApplication(reviewTarget.id, patch);
      await loadAll();
    } finally {
      setBusyId(null);
    }
  };
  const togglePause = async (app) => {
    setBusyId(app.id);
    try {
      await api.adminReferral.updateReferrer(app.id, { isPaused: !app.isPaused });
      await loadAll();
    } catch (err) {
      setError(err?.message || t('adminReferrals.failed'));
    } finally {
      setBusyId(null);
    }
  };

  if (forbidden) {
    return (
      <div className="rr-root">
        <div className="rr-setup card" style={{ marginTop: 60 }}>
          <h2>{t('adminReferrals.accessRequired')}</h2>
          <p>{t('adminReferrals.accessRequiredBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rr-root">
      <div className="rr-top">
        <div>
          <h1 className="rr-h">{t('adminReferrals.title')}</h1>
          <p className="rr-sub">{t('adminReferrals.subtitle')}</p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={loadAll}>
          <I.zap size={13} /> {t('adminReferrals.refresh')}
        </button>
      </div>

      {error && (
        <div className="rs-error" style={{ marginBottom: 4 }}>{error}</div>
      )}

      <Stats data={stats} />

      {v2 && (
        <ApplicationsTable
          rows={applications}
          scope={appsScope}
          setScope={setAppsScope}
          loading={loadingSignups}
          onReview={setReviewTarget}
          onTogglePause={togglePause}
          busyId={busyId}
        />
      )}

      {v2 && (
        <CreditsTable
          rows={credits}
          filter={creditFilter}
          setFilter={setCreditFilter}
          loading={loadingSignups}
        />
      )}

      <SignupsTable
        rows={signups}
        filter={signupFilter}
        setFilter={setSignupFilter}
        loading={loadingSignups}
      />

      <PayoutsTable
        rows={payouts}
        filter={payoutFilter}
        setFilter={setPayoutFilter}
        onMarkPaid={onMarkPaid}
        busyId={busyId}
      />

      <MarkPaidModal
        payout={markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onSubmit={submitMarkPaid}
      />

      {v2 && reviewTarget && (
        <ReviewModal
          app={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmit={submitReview}
        />
      )}
    </div>
  );
}

export default AdminReferrals;
